<template src="./DatePicker.html"></template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import dateFormat from "dateformat";
import { formatToISODateTime } from "@/services/dateFormatUtils";

export default defineComponent({
    inheritAttrs: false,
    emits: ["update:modelValue"],
    props: {
        modelValue: {
            type: String as PropType<string | undefined | null>,
            required: false
        },
        showTime: { type: Boolean, default: false },
        allowFieldInput: { type: Boolean, default: false },
        minDate: {
            type: String as PropType<string | undefined>,
            required: false
        },
        maxDate: {
            type: String as PropType<string | undefined>,
            required: false
        },
        requiredField: { type: Boolean, default: false }
    },
    computed: {
        dateValue: {
            get(): string | undefined | null {
                return this.modelValue;
            },
            set(v: string | undefined) {
                this.$emit("update:modelValue", v);
            }
        },
        isoDateValue(): string {
            if (this.dateValue) return formatToISODateTime(this.dateValue);
            return "";
        },
        displayValue(): string {
            if (!this.isoDateValue) return "";
            if (!this.showTime) {
                return dateFormat(
                    new Date(this.isoDateValue),
                    "dd.mm.yyyy",
                    true
                );
            }

            return dateFormat(
                new Date(this.isoDateValue),
                "dd.mm.yyyy HH:MM:ss"
            );
        },
        value: {
            get(): string {
                if (this.isoDateValue) {
                    return dateFormat(this.isoDateValue, "yyyy-mm-dd", true);
                }
                return "";
            },
            set(value: string) {
                // date picker returns local date, so utc: false
                this.dateValue = dateFormat(value, "yyyy-mm-dd", false);
            }
        },
        inputDate: {
            get(): string {
                return this.dateValue
                    ? dateFormat(this.dateValue, "yyyy-mm-dd", true)
                    : "";
            },
            set(value: string) {
                if (!value) {
                    this.dateValue = undefined;
                    return;
                }

                const parts = value.split("-");
                if (parts.length !== 3) return;
                const years = parts[0];
                if (years.length !== 4) return;

                this.dateValue = dateFormat(value, "yyyy-mm-dd", true);
            }
        },
        minDateValue(): string | undefined {
            if (this.minDate)
                return dateFormat(this.minDate, "yyyy-mm-dd", true);
            return undefined;
        },
        maxDateValue(): string | undefined {
            if (this.maxDate)
                return dateFormat(this.maxDate, "yyyy-mm-dd", true);
            return undefined;
        },
        dateRules(): undefined | [(v: string) => boolean] {
            return this.requiredField ? [(v: string) => !!v] : undefined;
        }
    },
    data() {
        return { menu: false };
    }
});
</script>

<style lang="scss" src="./DatePicker.scss"></style>
